import { Action } from "@ngrx/store";
import { DataSource } from "../../data-source/state/data-source.model";
import {
  CommonModelType,
  Model,
  ModelReleaseHistory,
  ModelRequest,
  ModelVersion,
} from "./model";

export enum ModelActionTypes {
  LoadModels = "[Model] Load Models",
  LoadModelsSuccess = "[Model] Load Models Success",

  AddModel = "[Model] Add Model",
  AddModelSuccess = "[Model] Add Model Success",

  UpdateModelLabel = "[Model] Update Model Label",
  UpdateModelLabelSuccess = "[Model] Update Model Label Success",

  UpdateModelVersionMessage = "[Model] Update Model Version Message",
  UpdateModelVersionData = "[Model] Update Model Version Data",
  UpdateReleaseContext = "[Model] Update Release Context",
  UpdateModelVersionDataAsync = "[Model] Update Model Version Data Async",
  UpdateModelVersionSuccess = "[Model] Update Model Version Success",

  MakeModelUnObsolete = "[Model] Make Model UnObsolete",
  MakeModelUnObsoleteSuccess = "[Model] Make Model UnObsolete Success",

  MakeModelObsolete = "[Model] Make Model Obsolete",
  MakeModelObsoleteSuccess = "[Model] Make Model Obsolete Success",

  DeleteModel = "[Model] Delete Model",
  DeleteModelSuccess = "[Model] Delete Model Success",

  DeleteModelVersion = "[Model] Delete Model Version",
  DeleteModelVersionSuccess = "[Model] Delete Model Version Success",

  LoadAllModelVersions = "[Model] Load All Model Versions",
  LoadAllModelVersionsSuccess = "[Model] Load All Model Versions Success",

  LoadModelVersions = "[Model] Load Model Versions",
  LoadModelVersionsSuccess = "[Model] Load Model Versions Success",

  LoadModelReleaseHistory = "[Model] Load Model Release History",
  LoadModelReleaseHistorySuccess = "[Model] Load Model Release History Success",

  ActionFailure = "[Model] Load or Update Model Failure",

  CloneModelVersion = "[Model] Clone Model Version",
  CloneModelVersionSuccess = "[Model] Clone Model Version Success",

  ReleaseModel = "[Model] release model",
  ReleaseModelSuccess = "[Model] release model success",

  UpdateModelDescription = "[Model] Update description of Model",

  ImportLexicon = "[Model] Import Lexicon",
  ImportLexiconSuccess = "[Model] Import Lexicon Success",
}

export class LoadModels implements Action {
  readonly type = ModelActionTypes.LoadModels;
}

export class LoadModelsSuccess implements Action {
  readonly type = ModelActionTypes.LoadModelsSuccess;

  constructor(public payload: { data: Model[] }) {}
}

export class AddModel implements Action {
  readonly type = ModelActionTypes.AddModel;

  constructor(public payload: ModelRequest) {}
}

export class AddModelSuccess implements Action {
  readonly type = ModelActionTypes.AddModelSuccess;

  constructor(public payload: Model) {}
}

export class UpdateModelLabel implements Action {
  readonly type = ModelActionTypes.UpdateModelLabel;

  constructor(
    public model: Model,
    public label: string,
  ) {}
}

export class UpdateModelLabelSuccess implements Action {
  readonly type = ModelActionTypes.UpdateModelLabelSuccess;

  constructor(public payload: Model) {}
}

export class UpdateModelVersionMessage implements Action {
  readonly type = ModelActionTypes.UpdateModelVersionMessage;

  constructor(
    public version: ModelVersion,
    public newMessage: string,
  ) {}
}

export class UpdateModelVersionSuccess implements Action {
  readonly type = ModelActionTypes.UpdateModelVersionSuccess;

  constructor(
    public modelVersion: ModelVersion,
    public unsaved: boolean = false,
  ) {}
}

export class UpdateModelVersionData implements Action {
  readonly type = ModelActionTypes.UpdateModelVersionData;

  constructor(
    public version: ModelVersion,
    public data: any,
    public updated: Date = new Date(),
  ) {}
}

export class UpdateReleaseContext implements Action {
  readonly type = ModelActionTypes.UpdateReleaseContext;

  constructor(
    public version: ModelVersion,
    public data: any,
    public updated: Date = new Date(),
  ) {}
}

export class UpdateModelVersionDataAsync implements Action {
  readonly type = ModelActionTypes.UpdateModelVersionDataAsync;

  constructor(
    public version: ModelVersion,
    public data: any,
    public updated: Date = new Date(),
  ) {}
}

export class MakeModelUnObsolete implements Action {
  readonly type = ModelActionTypes.MakeModelUnObsolete;

  constructor(public model: Model) {}
}

export class MakeModelUnObsoleteSuccess implements Action {
  readonly type = ModelActionTypes.MakeModelUnObsoleteSuccess;

  constructor(public model: Model) {}
}

export class MakeModelObsolete implements Action {
  readonly type = ModelActionTypes.MakeModelObsolete;

  constructor(public model: Model) {}
}

export class MakeModelObsoleteSuccess implements Action {
  readonly type = ModelActionTypes.MakeModelObsoleteSuccess;

  constructor(public model: Model) {}
}

export class DeleteModel implements Action {
  readonly type = ModelActionTypes.DeleteModel;

  constructor(public model: Model) {}
}

export class DeleteModelSuccess implements Action {
  readonly type = ModelActionTypes.DeleteModelSuccess;

  constructor(public modelId: string) {}
}

export class DeleteModelVersion implements Action {
  readonly type = ModelActionTypes.DeleteModelVersion;

  constructor(public modelVersion: ModelVersion) {}
}

export class DeleteModelVersionSuccess implements Action {
  readonly type = ModelActionTypes.DeleteModelVersionSuccess;

  constructor(public modelVersionId: string) {}
}

export class ActionFailure implements Action {
  readonly type = ModelActionTypes.ActionFailure;

  constructor(public error: any) {}
}

export class LoadAllModelVersions implements Action {
  readonly type = ModelActionTypes.LoadAllModelVersions;

  constructor() {}
}

export class LoadAllModelVersionsSuccess implements Action {
  readonly type = ModelActionTypes.LoadAllModelVersionsSuccess;

  constructor(public versions: ModelVersion[]) {}
}

export class LoadModelVersions implements Action {
  readonly type = ModelActionTypes.LoadModelVersions;

  constructor(public modelID: string) {}
}

export class LoadModelVersionsSuccess implements Action {
  readonly type = ModelActionTypes.LoadModelVersionsSuccess;

  constructor(public versions: ModelVersion[]) {}
}

export class LoadModelReleaseHistory implements Action {
  readonly type = ModelActionTypes.LoadModelReleaseHistory;

  constructor(public modelID: string) {}
}

export class LoadModelReleaseHistorySuccess implements Action {
  readonly type = ModelActionTypes.LoadModelReleaseHistorySuccess;

  constructor(public modelReleaseHistory: ModelReleaseHistory[]) {}
}

export class CloneModelVersion implements Action {
  readonly type = ModelActionTypes.CloneModelVersion;

  constructor(
    public model: Model,
    public message: string,
    public version: string,
  ) {}
}

export class CloneModelVersionSuccess implements Action {
  readonly type = ModelActionTypes.CloneModelVersionSuccess;

  constructor(public version: ModelVersion) {}
}

export class ReleaseModel implements Action {
  readonly type = ModelActionTypes.ReleaseModel;

  constructor(
    public modelID: string,
    public version: string,
  ) {}
}

export class ReleaseModelSuccess implements Action {
  readonly type = ModelActionTypes.ReleaseModelSuccess;
  constructor(public model: Model) {}
}

export class UpdateModelDescription implements Action {
  readonly type = ModelActionTypes.UpdateModelDescription;

  constructor(
    public model: Model,
    public description: string,
  ) {}
}

export class ImportLexicon implements Action {
  readonly type = ModelActionTypes.ImportLexicon;
  constructor(public fullLexicon: Object) {}
}

export class ImportLexiconSuccess implements Action {
  readonly type = ModelActionTypes.ImportLexiconSuccess;
  constructor() {}
}

export type ModelActions =
  | LoadModels
  | LoadModelsSuccess
  | AddModel
  | AddModelSuccess
  | UpdateModelLabel
  | UpdateModelLabelSuccess
  | MakeModelUnObsolete
  | MakeModelUnObsoleteSuccess
  | UpdateModelVersionMessage
  | UpdateModelVersionData
  | UpdateReleaseContext
  | UpdateModelVersionDataAsync
  | UpdateModelVersionSuccess
  | MakeModelObsolete
  | MakeModelObsoleteSuccess
  | DeleteModel
  | DeleteModelSuccess
  | DeleteModelVersion
  | DeleteModelVersionSuccess
  | ActionFailure
  | LoadAllModelVersions
  | LoadAllModelVersionsSuccess
  | LoadModelVersions
  | LoadModelVersionsSuccess
  | LoadModelReleaseHistory
  | LoadModelReleaseHistorySuccess
  | CloneModelVersion
  | CloneModelVersionSuccess
  | ReleaseModel
  | ReleaseModelSuccess
  | UpdateModelDescription
  | ImportLexicon
  | ImportLexiconSuccess;
